import { Eye, EyeOff, KeyRound, Lock, MoreHorizontal, RefreshCw, ShieldCheck, Trash2, Unlock, UserPlus, Users, X } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAdminToast } from "../../context/AdminToastContext";
import { adminErrorMessage, missingFieldsMessage } from "../../lib/adminFeedback";
import { supabase } from "../../lib/supabase";
import { AdminConfirmDialog } from "../../components/AdminConfirmDialog";
import { AdminSelect } from "../../components/AdminSelect";
import { writeAuditLog } from "../../lib/audit";
import { runAdminUserCommand } from "../../lib/adminUsersApi";
import type { AdminUser } from "../../types";
import { AdminPageSkeleton } from "../../components/LoadingSkeleton";

const emptyForm = () => ({
  email: "",
  display_name: "",
  role: "editor" as AdminUser["role"],
  password: "",
  passwordConfirmation: "",
});

const emptyPasswordReset = () => ({ password: "", passwordConfirmation: "" });

function generateSecurePassword(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*";
  const random = crypto.getRandomValues(new Uint32Array(16));
  return Array.from(random, (value) => alphabet[value % alphabet.length]).join("");
}

export function AdminUsersPage() {
  const { session, user, adminRole } = useAuth();
  const { showToast } = useAdminToast();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [accessBusy, setAccessBusy] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminUser | null>(null);
  const [passwordTarget, setPasswordTarget] = useState<AdminUser | null>(null);
  const [passwordReset, setPasswordReset] = useState(emptyPasswordReset);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [passwordResetBusy, setPasswordResetBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("admin_allowlist").select("*").order("created_at");
      if (error) showToast({ type: "error", title: "Admin-Team nicht geladen", message: adminErrorMessage(error, "Die Zugänge konnten nicht geladen werden.") });
      else {
        const nextAdmins = (data || []) as AdminUser[];
        setAdmins(nextAdmins);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (adminRole === "owner") void load(); }, [adminRole]);
  useEffect(() => {
    if (!openMenu) return;
    const closeMenu = () => setOpenMenu(null);
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") closeMenu(); };
    document.addEventListener("click", closeMenu);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("click", closeMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openMenu]);

  const createAdmin = async (event: FormEvent) => {
    event.preventDefault();
    if (adminRole !== "owner") {
      showToast({ type: "warning", title: "Inhaber-Recht erforderlich", message: "Nur Inhaber können neue Admin-Zugänge anlegen." });
      return;
    }
    const missing = [
      !form.display_name.trim() && "Name",
      !form.email.trim() && "E-Mail-Adresse",
      !form.password && "Startpasswort",
      !form.passwordConfirmation && "Passwort-Wiederholung",
    ].filter((field): field is string => Boolean(field));
    if (missing.length > 0) {
      showToast({ type: "warning", title: "Angaben fehlen", message: missingFieldsMessage(missing) });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      showToast({ type: "warning", title: "E-Mail-Adresse ungültig", message: "Bitte gib eine vollständige E-Mail-Adresse ein." });
      return;
    }
    if (form.password.length < 8) {
      showToast({ type: "warning", title: "Passwort zu kurz", message: "Das Startpasswort muss mindestens 8 Zeichen enthalten." });
      return;
    }
    if (form.password !== form.passwordConfirmation) {
      showToast({ type: "warning", title: "Passwörter stimmen nicht überein", message: "Bitte wiederhole das Startpasswort identisch." });
      return;
    }
    if (!session?.access_token) {
      showToast({ type: "error", title: "Sitzung abgelaufen", message: "Bitte melde dich erneut an und versuche es noch einmal." });
      return;
    }

    setBusy(true);
    const payload = {
      email: form.email.toLowerCase().trim(),
      display_name: form.display_name.trim(),
      role: form.role,
      password: form.password,
    };
    try {
      await runAdminUserCommand({ action: "create", ...payload }, session.access_token);

      await writeAuditLog({ action: "user.created", entityType: "user", entityId: payload.email, entityTitle: payload.display_name, details: { email: payload.email, role: payload.role } });
      showToast({ type: "success", title: "Benutzer angelegt", message: `${payload.display_name} kann sich jetzt direkt mit E-Mail und Startpasswort anmelden.` });
      setOpen(false);
      setShowPassword(false);
      setForm(emptyForm());
      await load();
    } catch (error) {
      showToast({ type: "error", title: "Benutzer nicht angelegt", message: adminErrorMessage(error, "Das Login-Konto konnte nicht erstellt werden.") });
    } finally {
      setBusy(false);
    }
  };

  const setAccess = async (admin: AdminUser, active: boolean) => {
    if (adminRole !== "owner") { showToast({ type: "warning", title: "Inhaber-Recht erforderlich", message: "Nur Inhaber können Admin-Zugänge sperren oder entsperren." }); return; }
    if (admin.email === user?.email?.toLowerCase()) { showToast({ type: "warning", title: "Aktion nicht möglich", message: "Du kannst deinen eigenen Zugang nicht sperren." }); return; }
    if (!active && !window.confirm(`${admin.display_name || admin.email} vorübergehend sperren?`)) return;

    setAccessBusy(admin.email);
    const { error } = await supabase.from("admin_allowlist").update({ active, updated_at: new Date().toISOString() }).eq("email", admin.email);
    setAccessBusy(null);
    setOpenMenu(null);
    if (error) showToast({ type: "error", title: active ? "Entsperren fehlgeschlagen" : "Sperren fehlgeschlagen", message: adminErrorMessage(error, "Der Zugangsstatus konnte nicht geändert werden.") });
    else {
      await writeAuditLog({ action: active ? "user.unlocked" : "user.locked", entityType: "user", entityId: admin.email, entityTitle: admin.display_name || admin.email, details: { email: admin.email, role: admin.role } });
      showToast({ type: "success", title: active ? "Zugang entsperrt" : "Zugang gesperrt", message: active ? `${admin.display_name || admin.email} kann den Adminbereich wieder verwenden.` : `${admin.display_name || admin.email} hat vorübergehend keinen Zugriff auf den Adminbereich.` });
      await load();
    }
  };

  const deleteAdmin = async (admin: AdminUser) => {
    if (adminRole !== "owner") { showToast({ type: "warning", title: "Inhaber-Recht erforderlich", message: "Nur Inhaber können Admin-Zugänge endgültig löschen." }); return; }
    if (admin.email === user?.email?.toLowerCase()) { showToast({ type: "warning", title: "Aktion nicht möglich", message: "Du kannst deinen eigenen Zugang nicht löschen." }); return; }
    if (!session?.access_token) { showToast({ type: "error", title: "Sitzung abgelaufen", message: "Bitte melde dich erneut an und versuche es noch einmal." }); return; }
    setAccessBusy(admin.email);
    try {
      await runAdminUserCommand({ action: "delete", email: admin.email }, session.access_token);

      setOpenMenu(null);
      setPendingDelete(null);
      await writeAuditLog({ action: "user.deleted", entityType: "user", entityId: admin.email, entityTitle: admin.display_name || admin.email, details: { email: admin.email, role: admin.role, wasActive: admin.active } });
      showToast({ type: "success", title: "Zugang gelöscht", message: `${admin.display_name || admin.email} wurde vollständig aus dem Admin-Team entfernt.` });
      await load();
    } catch (error) {
      showToast({ type: "error", title: "Löschen fehlgeschlagen", message: adminErrorMessage(error, "Der Admin-Zugang konnte nicht gelöscht werden.") });
    } finally {
      setAccessBusy(null);
    }
  };

  const closePasswordReset = () => {
    if (passwordResetBusy) return;
    setPasswordTarget(null);
    setPasswordReset(emptyPasswordReset());
    setShowResetPassword(false);
  };

  const resetAdminPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (!passwordTarget) return;
    if (adminRole !== "owner") { showToast({ type: "warning", title: "Inhaber-Recht erforderlich", message: "Nur Inhaber können Startpasswörter neu vergeben." }); return; }
    if (passwordTarget.email === user?.email?.toLowerCase()) { showToast({ type: "warning", title: "Eigenes Passwort", message: "Ändere dein eigenes Passwort über dein Konto in der oberen Leiste." }); return; }
    const missing = [
      !passwordReset.password && "neues Startpasswort",
      !passwordReset.passwordConfirmation && "Passwort-Wiederholung",
    ].filter((field): field is string => Boolean(field));
    if (missing.length > 0) { showToast({ type: "warning", title: "Angaben fehlen", message: missingFieldsMessage(missing) }); return; }
    if (passwordReset.password.length < 8) { showToast({ type: "warning", title: "Passwort zu kurz", message: "Das neue Startpasswort muss mindestens 8 Zeichen enthalten." }); return; }
    if (passwordReset.password !== passwordReset.passwordConfirmation) { showToast({ type: "warning", title: "Passwörter stimmen nicht überein", message: "Bitte wiederhole das neue Startpasswort identisch." }); return; }
    if (!session?.access_token) { showToast({ type: "error", title: "Sitzung abgelaufen", message: "Bitte melde dich erneut an und versuche es noch einmal." }); return; }

    setPasswordResetBusy(true);
    try {
      await runAdminUserCommand({ action: "reset_password", email: passwordTarget.email, password: passwordReset.password }, session.access_token);
      await writeAuditLog({ action: "user.password_reset", entityType: "user", entityId: passwordTarget.email, entityTitle: passwordTarget.display_name || passwordTarget.email, details: { email: passwordTarget.email, role: passwordTarget.role } });
      showToast({ type: "success", title: "Startpasswort neu vergeben", message: `${passwordTarget.display_name || passwordTarget.email} kann sich ab sofort mit dem neuen Startpasswort anmelden.` });
      setPasswordTarget(null);
      setPasswordReset(emptyPasswordReset());
      setShowResetPassword(false);
    } catch (error) {
      showToast({ type: "error", title: "Passwort nicht geändert", message: adminErrorMessage(error, "Das neue Startpasswort konnte nicht gespeichert werden.") });
    } finally {
      setPasswordResetBusy(false);
    }
  };

  const generatePassword = () => {
    const password = generateSecurePassword();
    setForm((current) => ({ ...current, password, passwordConfirmation: password }));
    setShowPassword(true);
    showToast({ type: "info", title: "Sicheres Passwort erzeugt", message: "Beide Passwortfelder wurden ausgefüllt. Übergib das Startpasswort sicher an die Person." });
  };

  const generateResetPassword = () => {
    const password = generateSecurePassword();
    setPasswordReset({ password, passwordConfirmation: password });
    setShowResetPassword(true);
    showToast({ type: "info", title: "Sicheres Startpasswort erzeugt", message: "Beide Passwortfelder wurden ausgefüllt. Übergib das neue Startpasswort sicher an die Person." });
  };

  if (adminRole !== "owner") return <Navigate replace to="/admin" />;
  if (loading) return <AdminPageSkeleton variant="cards" />;

  return (
    <>
      <div className="admin-page-heading"><div><span>Zugriffsverwaltung</span><h1>Admin-Team</h1><p>Lege Benutzer mit E-Mail-Adresse und persönlichem Startpasswort direkt an.</p></div><button className="admin-primary-button" onClick={() => setOpen(true)}><UserPlus /> Benutzer anlegen</button></div>
      <div className="admin-team-grid">
        {admins.map((admin) => {
          const canManage = admin.email !== user?.email?.toLowerCase();
          const menuOpen = openMenu === admin.email;
          return (
            <article className={`admin-team-card ${!admin.active ? "admin-team-card--inactive" : ""}`} key={admin.email}>
              <div className="team-card-top">
                <span className="team-avatar">{(admin.display_name || admin.email).split(/[ .@]/).filter(Boolean).map((part) => part[0]).slice(0, 2).join("").toUpperCase()}</span>
                {canManage && (
                  <div className="team-card-menu" onClick={(event) => event.stopPropagation()}>
                    <button
                      type="button"
                      className="team-menu-trigger"
                      disabled={accessBusy === admin.email}
                      aria-label={`Aktionen für ${admin.display_name || admin.email}`}
                      aria-expanded={menuOpen}
                      onClick={() => setOpenMenu((current) => current === admin.email ? null : admin.email)}
                    >
                      <MoreHorizontal />
                    </button>
                    {menuOpen && (
                      <div className="team-menu-popover" role="menu">
                        <button type="button" className="team-menu-action" role="menuitem" onClick={() => { setOpenMenu(null); setPasswordTarget(admin); setPasswordReset(emptyPasswordReset()); setShowResetPassword(false); }}>
                          <KeyRound />
                          <span><strong>Startpasswort neu vergeben</strong><small>Neues Passwort direkt festlegen</small></span>
                        </button>
                        <button type="button" className="team-menu-action" role="menuitem" onClick={() => void setAccess(admin, !admin.active)}>
                          {admin.active ? <Lock /> : <Unlock />}
                          <span><strong>{admin.active ? "Zugang sperren" : "Zugang entsperren"}</strong><small>{admin.active ? "Login vorübergehend deaktivieren" : "Admin-Zugriff wieder freigeben"}</small></span>
                        </button>
                        <button type="button" className="team-menu-action team-menu-action--danger" role="menuitem" onClick={() => { setOpenMenu(null); setPendingDelete(admin); }}>
                          <Trash2 />
                          <span><strong>Zugang löschen</strong><small>Login endgültig aus dem Admin-Team entfernen</small></span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <h2>{admin.display_name || admin.email.split("@")[0]}</h2>
              <p>{admin.email}</p>
              <div className="role-line"><span><ShieldCheck /> {roleLabel(admin.role)}</span><em className={admin.active ? "" : "locked"}>{admin.active ? "Aktiv" : "Gesperrt"}</em></div>
            </article>
          );
        })}
        <button className="add-team-card" onClick={() => setOpen(true)}><span><UserPlus /></span><strong>Weitere Person anlegen</strong><small>Login mit E-Mail und Startpasswort erstellen</small></button>
      </div>
      <section className="admin-panel permissions-panel"><div><Users /><div><h2>Rollen & Rechte</h2><p>Die Berechtigungen werden serverseitig geprüft.</p></div></div><dl><div><dt>Inhaber</dt><dd>Vollzugriff auf Inhalte, News, Benutzerverwaltung und Historie</dd></div><div><dt>Admin</dt><dd>News und sämtliche Seiteninhalte verwalten</dd></div><div><dt>Redaktion</dt><dd>Ausschließlich News-Beiträge erstellen und bearbeiten</dd></div></dl></section>
      {open && <div className="editor-overlay" role="dialog" aria-modal="true"><div className="invite-modal create-user-modal"><div className="editor-header"><div><span>Admin-Team erweitern</span><h2>Benutzer direkt anlegen</h2></div><button onClick={() => setOpen(false)} aria-label="Fenster schließen"><X /></button></div><form onSubmit={createAdmin} noValidate><span className="invite-icon"><KeyRound /></span><p>Das Konto ist anschließend sofort aktiv. Die Person kann sich direkt mit der angegebenen E-Mail-Adresse und dem Startpasswort anmelden.</p><label>Name<input value={form.display_name} onChange={(event) => setForm({ ...form, display_name: event.target.value })} placeholder="Vor- und Nachname" autoComplete="off" /></label><label>E-Mail-Adresse<input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="name@ttcrehlingen.de" autoComplete="off" /></label><label>Rolle<AdminSelect value={form.role} ariaLabel="Rolle des neuen Admin-Zugangs" options={[{ value: "editor", label: "Redaktion", description: "Kann Inhalte und Beiträge verwalten" }, { value: "admin", label: "Admin", description: "Erweiterte Verwaltung ohne Inhaberrechte" }, { value: "owner", label: "Inhaber", description: "Vollständiger Zugriff auf alle Bereiche" }]} onChange={(role) => setForm({ ...form, role })} /></label><div className="password-generator"><button type="button" onClick={generatePassword}><RefreshCw /> Sicheres Passwort erzeugen</button><small>Mindestens 8 Zeichen. Das Passwort wird nicht per E-Mail versendet.</small></div><label>Startpasswort<span className="password-input"><input type={showPassword ? "text" : "password"} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder="Mindestens 8 Zeichen" autoComplete="new-password" /><button type="button" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? "Passwort ausblenden" : "Passwort anzeigen"}>{showPassword ? <EyeOff /> : <Eye />}</button></span></label><label>Startpasswort wiederholen<input type={showPassword ? "text" : "password"} value={form.passwordConfirmation} onChange={(event) => setForm({ ...form, passwordConfirmation: event.target.value })} placeholder="Passwort erneut eingeben" autoComplete="new-password" /></label><div className="invite-actions"><button type="button" className="admin-secondary-button" onClick={() => setOpen(false)}>Abbrechen</button><button className="admin-primary-button" disabled={busy}><UserPlus /> {busy ? "Wird angelegt…" : "Benutzer anlegen"}</button></div></form></div></div>}
      {passwordTarget && <div className="editor-overlay" role="dialog" aria-modal="true" aria-labelledby="reset-password-dialog-title"><div className="invite-modal password-reset-modal"><div className="editor-header"><div><span>Inhaber-Funktion</span><h2 id="reset-password-dialog-title">Startpasswort neu vergeben</h2></div><button type="button" onClick={closePasswordReset} aria-label="Fenster schließen"><X /></button></div><form onSubmit={resetAdminPassword} noValidate><span className="invite-icon"><KeyRound /></span><p>Lege ein neues Startpasswort fest und übergib es anschließend sicher an die Person.</p><div className="password-account"><span>Admin-Zugang</span><strong>{passwordTarget.display_name || passwordTarget.email} · {passwordTarget.email}</strong></div><div className="password-generator"><button type="button" onClick={generateResetPassword}><RefreshCw /> Sicheres Passwort erzeugen</button><small>Das bisherige Passwort wird sofort ersetzt.</small></div><label>Neues Startpasswort<span className="password-input"><input type={showResetPassword ? "text" : "password"} value={passwordReset.password} onChange={(event) => setPasswordReset({ ...passwordReset, password: event.target.value })} placeholder="Mindestens 8 Zeichen" autoComplete="new-password" /><button type="button" onClick={() => setShowResetPassword((current) => !current)} aria-label={showResetPassword ? "Passwort ausblenden" : "Passwort anzeigen"}>{showResetPassword ? <EyeOff /> : <Eye />}</button></span></label><label>Startpasswort wiederholen<input type={showResetPassword ? "text" : "password"} value={passwordReset.passwordConfirmation} onChange={(event) => setPasswordReset({ ...passwordReset, passwordConfirmation: event.target.value })} placeholder="Passwort erneut eingeben" autoComplete="new-password" /></label><div className="invite-actions"><button type="button" className="admin-secondary-button" onClick={closePasswordReset} disabled={passwordResetBusy}>Abbrechen</button><button className="admin-primary-button" disabled={passwordResetBusy}><KeyRound /> {passwordResetBusy ? "Wird gespeichert…" : "Startpasswort speichern"}</button></div></form></div></div>}
      {pendingDelete && <AdminConfirmDialog title="Admin-Zugang löschen?" description="Das Login-Konto und der Eintrag im Admin-Team werden vollständig entfernt." subject={`${pendingDelete.display_name || pendingDelete.email} · ${pendingDelete.email}`} confirmLabel="Zugang löschen" busyLabel="Zugang wird gelöscht…" busy={accessBusy === pendingDelete.email} onCancel={() => setPendingDelete(null)} onConfirm={() => deleteAdmin(pendingDelete)} />}
    </>
  );
}

function roleLabel(role: AdminUser["role"]): string {
  return role === "owner" ? "Inhaber" : role === "admin" ? "Admin" : "Redaktion";
}
